/**
 * PHASE 3: AI Insight Engine v2 - Today Dashboard Widget
 * Displays the most relevant insight, anomaly, or breakthrough for today
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useIsPremiumUser } from '@/hooks/useIsPremiumUser';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Sparkles, TrendingUp, AlertTriangle, Award } from 'lucide-react';
import { format } from 'date-fns';

export function InsightOfTheDayWidget() {
  const { data: isPremium, isLoading: isPremiumLoading } = useIsPremiumUser();
  
  const { data: insight, isLoading } = useQuery({
    queryKey: ['insight-of-the-day'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return null;

      // Fetch the most recent and relevant insight
      const [breakthroughs, correlations, anomalies, predictions] = await Promise.all([
        supabase
          .from('breakthrough_events')
          .select('*')
          .eq('user_id', user.id)
          .is('celebrated_at', null)
          .order('achievement_date', { ascending: false })
          .limit(1),
        supabase
          .from('correlation_insights_v2')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1),
        supabase
          .from('anomaly_events')
          .select('*')
          .eq('user_id', user.id)
          .eq('requires_attention', true)
          .is('acknowledged_at', null)
          .order('detected_date', { ascending: false })
          .limit(1),
        supabase
          .from('trend_alerts_v2')
          .select('*')
          .eq('user_id', user.id)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
      ]);

      // Priority: Breakthroughs > Anomalies > Predictions > Correlations
      if (breakthroughs.data && breakthroughs.data.length > 0) {
        return { type: 'breakthrough', data: breakthroughs.data[0] };
      }
      if (anomalies.data && anomalies.data.length > 0) {
        return { type: 'anomaly', data: anomalies.data[0] };
      }
      if (predictions.data && predictions.data.length > 0) {
        return { type: 'prediction', data: predictions.data[0] };
      }
      if (correlations.data && correlations.data.length > 0) {
        return { type: 'correlation', data: correlations.data[0] };
      }

      return null;
    },
    enabled: isPremium === true // Only fetch when user is premium
  });

  // Hide widget for non-premium users
  if (isPremiumLoading || !isPremium) {
    return null;
  }

  if (isLoading) {
    return null; // Hide widget during loading
  }

  if (!insight) {
    return null; // Hide widget when no insights available
  }

  const getInsightDisplay = () => {
    const data = insight.data as any; // Type assertion for dynamic data structure
    switch (insight.type) {
      case 'breakthrough':
        return {
          icon: <Award className="h-5 w-5 text-primary" />,
          badge: <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">Breakthrough</Badge>,
          title: data.title,
          description: data.description,
          message: data.celebration_message,
          color: 'border-l-primary'
        };
      case 'anomaly':
        return {
          icon: <AlertTriangle className="h-5 w-5 text-accent" />,
          badge: <Badge variant="outline" className="bg-accent/10 text-accent border-accent/20">Early Warning</Badge>,
          title: `${data.metric_name} Alert`,
          description: data.description,
          message: data.severity === 'severe' ? 'This requires attention today.' : 'Keep an eye on this pattern.',
          color: 'border-l-accent'
        };
      case 'prediction':
        return {
          icon: <TrendingUp className="h-5 w-5 text-secondary" />,
          badge: <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary/20">Prediction</Badge>,
          title: data.title,
          description: data.description,
          message: data.actionable_advice,
          color: 'border-l-secondary'
        };
      case 'correlation':
        return {
          icon: <Sparkles className="h-5 w-5 text-soft-accent" />,
          badge: <Badge variant="outline" className="bg-soft-accent/10 text-soft-accent border-soft-accent/20">Pattern Detected</Badge>,
          title: `${data.metric_a} ↔ ${data.metric_b}`,
          description: data.insight_text,
          message: `This pattern has ${data.correlation_category} strength with ${Math.round(data.confidence_score * 100)}% confidence.`,
          color: 'border-l-soft-accent'
        };
      default:
        return null;
    }
  };

  const display = getInsightDisplay();
  if (!display) return null;

  return (
    <Card className={`p-6 border-l-4 ${display.color} bg-gradient-to-br from-background to-background/50`}>
      <div className="flex items-start gap-4">
        <div className="p-2 rounded-lg bg-muted/50">
          {display.icon}
        </div>
        <div className="flex-1 space-y-3">
          <div className="flex items-start justify-between gap-2">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {display.badge}
                <span className="text-xs text-muted-foreground">
                  {format(new Date(), 'dd MMM yyyy')}
                </span>
              </div>
              <h3 className="font-semibold text-lg text-foreground">{display.title}</h3>
            </div>
          </div>
          <p className="text-sm text-muted-foreground leading-relaxed">
            {display.description}
          </p>
          {display.message && (
            <p className="text-sm text-foreground/80 italic">
              {display.message}
            </p>
          )}
        </div>
      </div>
    </Card>
  );
}
